<script lang="ts" setup>
  import ComplaintChat from './ComplaintChat.vue';
  import type { Complaint, ComplaintAndRefundAddress, ComplaintItem } from '@storefront/resources';

  interface Props {
    complaint: Complaint;
    complaintReturnAddress: ComplaintAndRefundAddress;
    items: ComplaintItem[];
    showHeader?: boolean;
  }

  defineProps<Props>();
</script>
<template>
  <div class="complaint-details">
    <h1 v-if="showHeader">
      {{ $t('SHOP.COMPLAINT.HEADER.COMPLAINT_DETAILS') }}
    </h1>
    <SfTile large>
      <template #title>
        <div class="complaint-details__header-wrapper">
          <div class="complaint-details__header">
            <h2 class="account__heading">
              {{ $t('SHOP.COMPLAINT.HEADER.COMPLAINT_NR', { number: complaint.number }) }}
            </h2>
            <span class="complaint-details__date">
              {{ $t('SHOP.COMPLAINT.HEADER.COMPLAINT_DATE') }}
              <SfDateTime :value="complaint.createdAt" />
            </span>
          </div>
          <div class="order__status">
            <SfComplaintStatus :status="complaint.status" />
          </div>
        </div>
      </template>
      <template #default>
        <div class="complaint-details__items">
          <SfListCard v-for="item in items" class="item" large>
            <template #image>
              <MediaPicture :photo="item.photo" sizes="140x140 2x, 70x70" />
            </template>
            <template #default>
              <div class="item__content">
                <h2 class="item__heading" v-html="item.title" />
                <p v-if="item.sku" class="item__sku" v-html="$t('SHOP.COMPLAINT.LABEL.SKU', { code: item.sku })" />
                <p v-if="item.serialNumber" class="item__serial-number" v-html="$t('SHOP.COMPLAINT.LABEL.SERIAL_NUMBER', { number: item.serialNumber })" />
              </div>
            </template>
          </SfListCard>
        </div>
      </template>
    </SfTile>

    <SfTile>
      <div class="complaint-details__data">
        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.COMPLAINT.HEADER.COMPLAINT_CUSTOMER_DETAILS') }}</h3>
          </template>
          <address>
            <p v-if="complaint.name">{{ complaint.name }}</p>
            <p v-if="complaint.firstLine">{{ complaint.firstLine }}</p>
            <p v-if="complaint.secondLine">{{ complaint.secondLine }}</p>
            <p v-if="complaint.thirdLine">{{ complaint.thirdLine }}</p>
            <p v-if="complaint.countryCode">{{ complaint.countryCode }}</p>
            <p v-if="complaint.email">{{ complaint.email }}</p>
            <p v-if="complaint.phone">{{ complaint.phone }}</p>
          </address>
        </SfTile>
        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.COMPLAINT.HEADER.RETURN_ADDRESS') }}</h3>
          </template>
          <address>
            <p v-if="complaintReturnAddress.company">{{ complaintReturnAddress.company }}</p>
            <p v-if="complaintReturnAddress.name">{{ complaintReturnAddress.name }}</p>
            <p v-if="complaintReturnAddress.firstLine">{{ complaintReturnAddress.firstLine }}</p>
            <p v-if="complaintReturnAddress.secondLine">{{ complaintReturnAddress.secondLine }}</p>
            <p v-if="complaintReturnAddress.thirdLine">{{ complaintReturnAddress.thirdLine }}</p>
            <p v-if="complaintReturnAddress.countryCode">{{ complaintReturnAddress.countryCode }}</p>
            <p v-if="complaintReturnAddress.email">{{ complaintReturnAddress.email }}</p>
            <p v-if="complaintReturnAddress.phone">{{ complaintReturnAddress.phone }}</p>
          </address>
        </SfTile>

        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.COMPLAINT.HEADER.COMPLAINT_DETAILS') }}</h3>
          </template>
          <p><strong>{{ $t('SHOP.COMPLAINT.LABEL.REASON') }}</strong> {{ complaint.reason }}</p>
          <p><strong>{{ $t('SHOP.COMPLAINT.LABEL.DESCRIPTION') }}</strong> {{ complaint.description }}</p>
        </SfTile>

        <SfTile small>
          <template #title>
            <h3>{{ $t('SHOP.COMPLAINT.HEADER.SELECTED_RETURN_METHOD') }}</h3>
          </template>
          <p><strong>{{ $t('SHOP.COMPLAINT.LABEL.RETURN_METHOD_NAME') }}</strong>{{ complaint.returnMethod.name }}</p>
          <p><strong>{{ $t('SHOP.COMPLAINT.LABEL.RETURN_METHOD_DESCRIPTION') }}</strong>{{ complaint.returnMethod.description }}</p>
        </SfTile>
      </div>
      <SfTile v-if="complaint.files && complaint.files?.length > 0" small>
        <template #title>
          <h3>{{ $t('SHOP.COMPLAINT.HEADER.ATTACHMENTS') }}</h3>
        </template>
        <ul class="complaint-details__files">
          <li v-for="file in complaint.files">
            <a :href="file.raw || $route('public.media.file.download', { filename: file.filename })" target="_blank">
              <span class="icon">
                <SfIconDocument aria-hidden="true" />
              </span>
              <span class="name">
                {{ file.displayName || file.original || file.filename }}
              </span>
            </a>
          </li>
        </ul>
      </SfTile>
    </SfTile>
    <SfTile small>
      <ComplaintChat :complaint />
    </SfTile>
  </div>
</template>

<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .complaint-details {
    display: flex;
    flex-direction: column;
    row-gap: 2rem;

    h3 {
      font-size: 1.125rem;
    }

    p {
      font-size: 0.875rem;
    }

    &__data {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      align-items: flex-start;
      gap: 2rem;

      @include media.query(xl) {
        grid-template-columns: repeat(3, 1fr);
      }

      @include media.query(md) {
        grid-template-columns: repeat(2, 1fr);
      }

      @include media.query(md) {
        grid-template-columns: repeat(1, 1fr);
      }
    }

    &__items {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__files {
      line-height: 1.5rem;
      list-style: none;
      padding: 0;
      margin: 0;

      a {
        display: flex;
        align-items: flex-start;
        line-height: 1rem;
        font-size: 0.875rem;
        gap: 0.5rem;

        svg {
          display: inline-block;
          font-size: 1rem;
        }
      }
    }

    &__header-wrapper {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      width: 100%;
    }

    @include media.query(md) {
      gap: 1rem;

      h3 {
        font-size: 1rem;
      }

      &__header-wrapper {
        flex-direction: column;
        justify-content: flex-start;
      }
    }
  }

  .item {
    &__heading {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
    }

    &__sku,
    &__serial-number {
      font-size: 0.75rem;
      color: var(--secondary-color);
    }
  }
</style>
